/**
 * Product Registry — single source of truth for resolving any ARI/ATI
 * to its product, display name, icons, and resource types.
 *
 * Usage:
 *   resolveProduct('ari:cloud:confluence:abc:page/123')
 *   → { resourceOwner: 'confluence', displayName: 'Confluence', logo: {...}, ... }
 *
 *   getProductDisplayName('ari:cloud:townsquare:abc:goal/123')
 *   → 'Home'
 *
 *   getProductKey('ari:third-party:slack.slack::message/...')
 *   → 'slack.slack'
 */

// The product registry JSON is generated from resource-owners.yaml
// and enriched with icons from @atlaskit/logo, @atlaskit/icon, and thesvg.
import registryData from '../../product-registry.json';

// ============================================================================
// Types
// ============================================================================

export interface ProductLogoRef {
	source: 'atlaskit-logo' | 'atlaskit-raw-icon' | 'thesvg' | 'asset' | 'custom';
	importPath: string;
	exportName: string;
	svgKey?: string;
	displayAs?: string;
}

export interface SubItemIconRef {
	source: 'atlaskit-icon' | 'atlaskit-icon-lab';
	importPath: string;
	exportName: string;
}

export interface ResourceTypeInfo {
	resourceType: string;
	template: string;
	icon: SubItemIconRef | null;
}

export interface ProductInfo {
	resourceOwner: string;
	platform: string;
	isThirdParty: boolean;
	displayName: string;
	logo: ProductLogoRef | null;
	notNeeded: boolean;
	resourceTypes: Record<string, ResourceTypeInfo>;
}

interface ProductRegistry {
	_meta: {
		description: string;
		usage: string;
		ariFormat: string;
		atiFormat: string;
		generatedAt: string;
	};
	products: Record<string, ProductInfo>;
}

// ============================================================================
// Registry access
// ============================================================================

const registry = registryData as unknown as ProductRegistry;

/**
 * Extract the resource owner key from an ARI or ATI string.
 * ARI format: ari:{platform}:{resourceOwner}:{qualifier}:{resourceType}/{id}
 * ATI format: ati:{platform}:{resourceOwner}:{resourceType}
 */
export function getProductKey(ariOrAti: string): string | null {
	if (!ariOrAti || typeof ariOrAti !== 'string') {
		return null;
	}
	const parts = ariOrAti.split(':');
	if (parts.length < 3) {
		return null;
	}
	return parts[2] || null;
}

/**
 * Resolve a product from an ARI or ATI string.
 * Returns the full ProductInfo or null if not found.
 */
export function resolveProduct(ariOrAti: string): ProductInfo | null {
	const key = getProductKey(ariOrAti);
	if (!key) {
		return null;
	}
	return registry.products[key] ?? null;
}

/**
 * Resolve a product from a resource owner key directly.
 */
export function resolveProductByKey(resourceOwner: string): ProductInfo | null {
	return registry.products[resourceOwner] ?? null;
}

/**
 * Get the display name for a resource owner key directly.
 */
export function getProductDisplayNameByKey(resourceOwner: string): string {
	const product = resolveProductByKey(resourceOwner);
	if (product) {
		return product.displayName;
	}
	return capitalise(resourceOwner);
}

/**
 * Resolve a product from a discovered node — tries the node's ARI first,
 * then falls back to the node's integration field.
 */
export function resolveProductForNode(node: {
	id: string;
	integration?: string;
	thirdPartyId?: string;
}): ProductInfo | null {
	const ariKey = getProductKey(node.id);

	// 1. For generic resource owners, prefer integration
	if (node.integration && (!ariKey || GENERIC_RESOURCE_OWNERS.has(ariKey))) {
		const byKey = resolveProductByKey(node.integration);
		if (byKey) {
			return byKey;
		}
		const match = findProductByDisplayName(node.integration);
		if (match) {
			return match;
		}
	}

	// 1b. For generic resource owners (graph, identity, devops) where
	//     `integration` is missing but the node carries a third-party
	//     ARI in `properties.thirdPartyId` (e.g.
	//     `ari:third-party:google.google-calendar::…`), use that ARI to
	//     resolve the connector. This is the primary lookup path for
	//     calendar events / Google Docs / Slack messages / Loom videos
	//     etc. whose GraphQL fragment never populates `productDisplayName`.
	if (node.thirdPartyId && (!ariKey || GENERIC_RESOURCE_OWNERS.has(ariKey))) {
		const byTpId = getProductForThirdPartyId(node.thirdPartyId);
		if (byTpId) {
			return byTpId;
		}
	}

	// 2. Try resolving from the node's ARI
	const product = resolveProduct(node.id);
	if (product) {
		return product;
	}

	// 3. Try the integration field for remaining cases
	if (node.integration) {
		const byKey = resolveProductByKey(node.integration);
		if (byKey) {
			return byKey;
		}
		const match = findProductByDisplayName(node.integration);
		if (match) {
			return match;
		}
	}

	// 4. Final fallback to thirdPartyId for any remaining case.
	if (node.thirdPartyId) {
		const byTpId = getProductForThirdPartyId(node.thirdPartyId);
		if (byTpId) {
			return byTpId;
		}
	}

	return null;
}

/**
 * Get the product key for a node — used for clustering.
 * Returns the resource owner key (e.g. 'confluence', 'slack.slack', 'graph')
 * or a fallback based on integration.
 */
/**
 * Generic resource owners that host entities from multiple integrations.
 * For these, the integration field (from provider enrichment) should take
 * priority over the ARI-based product key.
 *
 * e.g. `ari:cloud:graph::commit/...` has ariKey "graph" but the actual
 * product is "Bitbucket" or "GitHub" depending on the integration.
 */
const GENERIC_RESOURCE_OWNERS = new Set(['graph', 'identity', 'devops']);

export function getProductKeyForNode(node: {
	id: string;
	integration?: string;
	thirdPartyId?: string;
}): string {
	const ariKey = getProductKey(node.id);

	// 1. For generic resource owners (graph, identity, devops), prefer
	//    the integration field — it tells us the actual product (Slack,
	//    Bitbucket, Google, etc.) rather than the hosting platform.
	if (node.integration && (!ariKey || GENERIC_RESOURCE_OWNERS.has(ariKey))) {
		const product = findProductByDisplayName(node.integration);
		if (product) {
			return product.resourceOwner;
		}
		return node.integration.toLowerCase();
	}

	// 1b. Same special-casing for graph-bridged nodes whose only
	//     connector signal is `properties.thirdPartyId`.
	if (node.thirdPartyId && (!ariKey || GENERIC_RESOURCE_OWNERS.has(ariKey))) {
		const product = getProductForThirdPartyId(node.thirdPartyId);
		if (product) {
			return product.resourceOwner;
		}
	}

	// 2. From ARI — for non-generic products (confluence, jira, loom, etc.)
	if (ariKey && registry.products[ariKey]) {
		return ariKey;
	}

	// 3. From integration field (for any remaining cases)
	if (node.integration) {
		const product = findProductByDisplayName(node.integration);
		if (product) {
			return product.resourceOwner;
		}
		return node.integration.toLowerCase();
	}

	// 4. From thirdPartyId for any remaining case.
	if (node.thirdPartyId) {
		const product = getProductForThirdPartyId(node.thirdPartyId);
		if (product) {
			return product.resourceOwner;
		}
	}

	// 5. Fallback to ARI key even if not in registry
	return ariKey ?? 'unknown';
}

/**
 * Get the resource type from an ARI string.
 * ARI format: ari:{platform}:{resourceOwner}:{qualifier}:{resourceType}/{id}
 */
export function getResourceType(ari: string): string | null {
	if (!ari || typeof ari !== 'string') {
		return null;
	}
	const parts = ari.split(':');
	if (parts.length < 5) {
		return null;
	}
	const resourceWithId = parts[4]; // e.g. "page/123"
	return resourceWithId?.split('/')[0] ?? null;
}

/**
 * Find a product by its display name (case-insensitive).
 */
function findProductByDisplayName(displayName: string): ProductInfo | null {
	const lower = displayName.toLowerCase();
	for (const product of Object.values(registry.products)) {
		if (product.displayName.toLowerCase() === lower) {
			return product;
		}
	}
	return null;
}

function capitalise(s: string): string {
	if (!s) {
		return '';
	}
	return s
		.split(/[-.]/)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(' ');
}

// ============================================================================
// Secondary lookup index for connector slugs
// ============================================================================

/**
 * `thirdPartyId` values returned by AGG frequently encode the connector as
 * either `vendor.product` (e.g. `github.github`, `dropbox.dropbox`) or a
 * bare product slug (`github`, `slack`). Build a secondary lookup keyed on
 * the trailing segment so both shapes resolve to the same product entry.
 */
const PRODUCTS_BY_TRAILING_SEGMENT: Record<string, ProductInfo> = (() => {
	const index: Record<string, ProductInfo> = {};
	for (const product of Object.values(registry.products)) {
		const owner = product.resourceOwner;
		const trailing = owner.includes('.') ? owner.split('.').pop() : owner;
		if (trailing && !index[trailing]) {
			index[trailing] = product;
		}
	}
	return index;
})();

/**
 * Resolve the product implied by a `thirdPartyId` property on an external
 * graph entity.
 *
 * The `thirdPartyId` values returned by the graph store come in several
 * shapes that we normalise to a connector key:
 *
 *   - `ari:cloud:bitbucket::pullrequest/abc:123`    (ARI form)
 *   - `ari:third-party:github.github:repository/abc`
 *   - `bitbucket::pullrequest/123`                   (`<connector>::<type>/<id>`)
 *   - `github.github::repository/abc`                (`<vendor>.<product>::<type>/<id>`)
 *   - `dropbox.dropbox`                              (bare resource owner)
 *   - `bitbucket`                                    (bare connector)
 *
 * Ported from the home explorer so the shared `cypherQueryService` can
 * inject a `productDisplayName` onto External graph entities without
 * depending on the richer simple-home icon registry.
 */
export function getProductForThirdPartyId(
	thirdPartyId: string | null | undefined,
): ProductInfo | null {
	if (!thirdPartyId) return null;

	if (thirdPartyId.startsWith('ari:')) {
		const fromAri = resolveProduct(thirdPartyId);
		if (fromAri) return fromAri;

		// `ari:third-party:<vendor>.<product>:...` and the related
		// `ari:cloud:graph::<resourceType>/...` shapes don't classify under
		// a registry product key directly (`third-party` / `graph` aren't
		// products). For these, the connector identity lives at index 2 of
		// the ARI segments (e.g. `bitbucket.bitbucket`). Extract that and
		// fall through to the resource-owner / trailing-segment lookups
		// below so we still resolve a product.
		const ariParts = thirdPartyId.split(':');
		const ariClassifier = ariParts[1];
		const ariResourceOwner = ariParts[2];
		if (
			(ariClassifier === 'third-party' || ariClassifier === 'cloud') &&
			ariResourceOwner &&
			registry.products[ariResourceOwner]
		) {
			return registry.products[ariResourceOwner];
		}
		// Defer to the connector-token path below using the resource owner
		// (e.g. `bitbucket.bitbucket`) instead of a noisy raw token like
		// `ari` that the simple split would otherwise produce.
		if (ariResourceOwner) {
			return resolveByConnectorToken(ariResourceOwner);
		}
	}

	const connectorToken = thirdPartyId.split(/::|:|\//)[0] ?? thirdPartyId;
	return resolveByConnectorToken(connectorToken);
}

function resolveByConnectorToken(connectorToken: string | null | undefined): ProductInfo | null {
	if (!connectorToken) return null;

	const direct = registry.products[connectorToken];
	if (direct) return direct;

	if (connectorToken.includes('.')) {
		const trailing = connectorToken.split('.').pop();
		if (trailing && PRODUCTS_BY_TRAILING_SEGMENT[trailing]) {
			return PRODUCTS_BY_TRAILING_SEGMENT[trailing];
		}
		const leading = connectorToken.split('.')[0];
		if (leading && PRODUCTS_BY_TRAILING_SEGMENT[leading]) {
			return PRODUCTS_BY_TRAILING_SEGMENT[leading];
		}
	} else if (PRODUCTS_BY_TRAILING_SEGMENT[connectorToken]) {
		return PRODUCTS_BY_TRAILING_SEGMENT[connectorToken];
	}

	return null;
}
